import { Modal, Text } from '@/components';
import { VITE_RELEASE_VERSION } from '@/config';
import { useFeatureFlags } from '@/features/feature-flag/_hooks/feature-flags';
import { t } from '@/i18n';
import { useRevisionListenerStatus } from '@/providers';
import {
  // TODO: dev 環境での検証終われば削除
  // isDevMode,
  logger,
} from '@/utils';
import { type FC, useCallback, useEffect, useRef } from 'react';
import { ZodError, z } from 'zod';

const REVISION_LISTENING_INTERVAL = 5 * 60 * 1000;
const REVISION_FILE_URL = '/revision.json';

const RevisionInfoModel = z.object({
  revision: z.string(),
});

const getRevision = async () => {
  try {
    const response = await fetch(REVISION_FILE_URL, {
      cache: 'no-store',
    });
    if (!response.ok) {
      return VITE_RELEASE_VERSION;
    }

    return RevisionInfoModel.parse(await response.json()).revision;
  } catch (e) {
    if (e instanceof ZodError) {
      logger.error('failed to parse', e);

      return null;
    }
    return VITE_RELEASE_VERSION;
  }
};

const Timer: FC<{
  onSuggest: () => void;
}> = ({ onSuggest }) => {
  const timer = useRef<NodeJS.Timeout | null>(null);

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const checkRevision = useCallback(async () => {
    const latestRevision = await getRevision();

    if (latestRevision === null) {
      return;
    }

    if (VITE_RELEASE_VERSION !== latestRevision) {
      onSuggest();
      return;
    }

    timer.current = setTimeout(() => {
      checkRevision();
    }, REVISION_LISTENING_INTERVAL);
  }, [onSuggest]);

  useEffect(() => {
    checkRevision();

    return clearTimer;
  }, [checkRevision, clearTimer]);

  // Note:
  // To start listening to the revision when the tab is focused
  // To stop listening to the revision when the tab is unfocused
  useEffect(() => {
    const handler = () => {
      if (document.hidden) {
        clearTimer();
      } else {
        checkRevision();
      }
    };

    document.addEventListener('visibilitychange', handler);

    return () => {
      document.removeEventListener('visibilitychange', handler);
    };
  }, [clearTimer, checkRevision]);

  return <></>;
};

export const RevisionListener: FC = () => {
  const { features } = useFeatureFlags();
  const { revisionListenerStatus, setRevisionListenerStatus } =
    useRevisionListenerStatus();

  useEffect(() => {
    const shouldListen =
      // TODO: dev 環境での検証終われば削除
      location.href.indexOf('refreshSpaTest') !== -1 &&
      // TODO: dev 環境での検証終わればコメントアウト解除
      // !isDevMode() &&
      VITE_RELEASE_VERSION !== 'unknown' &&
      features?.revisionListener;

    if (revisionListenerStatus === 'ready' && shouldListen) {
      setRevisionListenerStatus('listening');
    }
  }, [revisionListenerStatus, features, setRevisionListenerStatus]);

  return revisionListenerStatus !== 'ready' ? (
    <>
      {revisionListenerStatus === 'listening' && (
        <Timer onSuggest={() => setRevisionListenerStatus('suggesting')} />
      )}
      <Modal
        destroyOnClose
        open={revisionListenerStatus === 'suggesting'}
        title={t('revision-listener-title-contents-might-be-old')}
        onOk={() => {
          location.reload();
        }}
        okText={t('revision-listener-button-label-execute-reloading')}
        cancelText={t(
          'revision-listener-button-label-do-not-execute-reloading',
        )}
        onCancel={() => setRevisionListenerStatus('suggested')}
        closable={false}
        maskClosable={false}
      >
        <Text
          text={t('revision-listener-title-contents-message-suggest-reloading')}
        />
      </Modal>
    </>
  ) : null;
};
